import { ITradein } from '@/models/ITradein'

// El tradein llega de la API, así que un campo "sin valor" puede venir en null, en undefined
// (la columna no viaja en el select) o en ''. Los tres significan lo mismo y una comparación
// estricta contra null se equivoca en los otros dos casos.
const hasValue = (value: string | null | undefined): boolean =>
  value !== null && value !== undefined && value !== ''

// docusign_id se escribe al generar el contrato. Con el contrato ya generado no se reprocesa
// ni la batería ni la evaluación IA.
export const isContractGenerated = (tradein: ITradein): boolean => hasValue(tradein.docusign_id)

// grade_quality = grado IA + sufijo de batería, y es el marcador de «el paso de batería quedó cerrado».
//
// Con switch: queda sin valor hasta que guarden el estado de la batería (lo escribe
// updateBatteryHealth), así que sirve para saber si el paso está pendiente.
// Sin switch: lo escribe la propia evaluación IA en su primera corrida, así que ahí no indica
// que se haya hecho un paso posterior y no se usa como marcador.
//
// Por eso pending y done son ambos false fuera del flujo con switch, y no uno la negación del otro.
//
// showSwitchBattery: true = se muestra el switch, false = no se muestra,
// null = el catálogo de modelos con batería aún no responde.
export const hasGradeQuality = (tradein: ITradein): boolean => hasValue(tradein.grade_quality)

export const isBatteryStepDone = (tradein: ITradein, showSwitchBattery: boolean | null): boolean =>
  showSwitchBattery === true && hasGradeQuality(tradein)

export const isBatteryStepPending = (tradein: ITradein, showSwitchBattery: boolean | null): boolean =>
  showSwitchBattery === true && !hasGradeQuality(tradein)

// El reanálisis de la IA se cierra cuando ya se hizo el paso siguiente, y cuál es ese paso depende
// de con qué componente se evalúa la batería. Ojo que showSwitchBattery === false junta dos casos
// distintos (Discounts y «sin paso de batería»), así que no alcanza por sí solo:
//
// - SwitchBattery (showSwitchBattery === true): lo marca grade_quality.
// - Discounts (useDiscounts, el flujo de Apple con % de salud): lo marca isBatteryHealthy, que
//   escribe ese guardado. Hay que respetarlo porque Discounts persiste el nextGrade y el descuento
//   calculados sobre el grade_IA vigente, y reanalizar después los dejaría desactualizados.
// - Ninguno de los dos: no hay paso siguiente que proteger, se puede reanalizar hasta el contrato.
export const canReanalyzeIA = (
  tradein: ITradein,
  showSwitchBattery: boolean | null,
  useDiscounts: boolean
): boolean => {
  if (isContractGenerated(tradein)) return false
  if (useDiscounts) return tradein.isBatteryHealthy === null
  return !isBatteryStepDone(tradein, showSwitchBattery)
}
